import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = ({ currentLanguage }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const translations = {
    en: {
      title: "Frequently Asked Questions",
      faqs: [
        {
          question: "How is the debt-free date calculated?",
          answer: "The debt-free date is calculated by dividing your total debt by your monthly payment amount. This gives us the number of months needed to pay off the debt, which we then add to today's date to determine your exact debt-free date."
        },
        {
          question: "What do the status indicators mean?",
          answer: "ON TRACK means your debt will be paid off within 36 months with sustainable payments.\nSTRUGGLING indicates your payoff timeline extends beyond 36 months but remains achievable.\nUNSUSTAINABLE means your monthly payment is too low relative to your debt amount, resulting in an unrealistic payoff timeline."
        },
        {
          question: "Should I include all types of debt?",
          answer: "Yes, include all consumer debt such as credit cards, personal loans, auto loans, and student loans. However, mortgage debt is typically calculated separately due to its long-term nature and different interest structures."
        },
        {
          question: "How can I improve my debt payoff timeline?",
          answer: "You can accelerate debt freedom by increasing your monthly payment amount, consolidating high-interest debts, reducing unnecessary expenses to free up more money for debt payments, or using windfalls like tax refunds or bonuses for extra payments."
        },
        {
          question: "Does this calculator account for interest?",
          answer: "This calculator provides a simplified timeline based on your current payment amount. For precise calculations including interest rates and compound interest effects, consult with a financial advisor or use specialized debt payoff tools."
        }
      ]
    },
    es: {
      title: "Preguntas Frecuentes",
      faqs: [
        {
          question: "¿Cómo se calcula la fecha libre de deudas?",
          answer: "La fecha libre de deudas se calcula dividiendo su deuda total por el monto de su pago mensual. Esto nos da el número de meses necesarios para pagar la deuda, que luego agregamos a la fecha de hoy para determinar su fecha exacta libre de deudas."
        },
        {
          question: "¿Qué significan los indicadores de estado?",
          answer: "EN CAMINO significa que su deuda se pagará dentro de 36 meses con pagos sostenibles.\nLUCHANDO indica que su cronograma de pago se extiende más allá de 36 meses pero sigue siendo alcanzable.\nINSOSTENIBLE significa que su pago mensual es demasiado bajo en relación con el monto de su deuda, lo que resulta en un cronograma de pago poco realista."
        },
        {
          question: "¿Debo incluir todos los tipos de deuda?",
          answer: "Sí, incluya todas las deudas de consumo como tarjetas de crédito, préstamos personales, préstamos de automóviles y préstamos estudiantiles. Sin embargo, la deuda hipotecaria generalmente se calcula por separado debido a su naturaleza a largo plazo y diferentes estructuras de interés."
        },
        {
          question: "¿Cómo puedo mejorar mi cronograma de pago de deudas?",
          answer: "Puede acelerar la libertad de deudas aumentando el monto de su pago mensual, consolidando deudas de alto interés, reduciendo gastos innecesarios para liberar más dinero para pagos de deudas, o usando ganancias inesperadas como reembolsos de impuestos o bonificaciones para pagos adicionales."
        },
        {
          question: "¿Este calculador tiene en cuenta los intereses?",
          answer: "Este calculador proporciona un cronograma simplificado basado en el monto de su pago actual. Para cálculos precisos que incluyan tasas de interés y efectos de interés compuesto, consulte con un asesor financiero o use herramientas especializadas de pago de deudas."
        }
      ]
    },
    pt: {
      title: "Perguntas Frequentes",
      faqs: [
        {
          question: "Como é calculada a data livre de dívidas?",
          answer: "A data livre de dívidas é calculada dividindo sua dívida total pelo valor do seu pagamento mensal. Isso nos dá o número de meses necessários para pagar a dívida, que então adicionamos à data de hoje para determinar sua data exata livre de dívidas."
        },
        {
          question: "O que significam os indicadores de status?",
          answer: "NO CAMINHO significa que sua dívida será paga dentro de 36 meses com pagamentos sustentáveis.\nLUTANDO indica que seu cronograma de pagamento se estende além de 36 meses, mas permanece alcançável.\nINSUSTENTÁVEL significa que seu pagamento mensal é muito baixo em relação ao valor da sua dívida, resultando em um cronograma de pagamento irrealista."
        },
        {
          question: "Devo incluir todos os tipos de dívida?",
          answer: "Sim, inclua todas as dívidas de consumo, como cartões de crédito, empréstimos pessoais, empréstimos de automóveis e empréstimos estudantis. No entanto, a dívida hipotecária é normalmente calculada separadamente devido à sua natureza de longo prazo e diferentes estruturas de juros."
        },
        {
          question: "Como posso melhorar meu cronograma de pagamento de dívidas?",
          answer: "Você pode acelerar a liberdade da dívida aumentando o valor do seu pagamento mensal, consolidando dívidas de juros altos, reduzindo despesas desnecessárias para liberar mais dinheiro para pagamentos de dívidas, ou usando ganhos inesperados como restituições de impostos ou bônus para pagamentos extras."
        },
        {
          question: "Este calculador leva em conta os juros?",
          answer: "Este calculador fornece um cronograma simplificado com base no valor do seu pagamento atual. Para cálculos precisos incluindo taxas de juros e efeitos de juros compostos, consulte um consultor financeiro ou use ferramentas especializadas de pagamento de dívidas."
        }
      ]
    },
    fr: {
      title: "Questions Fréquemment Posées",
      faqs: [
        {
          question: "Comment la date libre de dettes est-elle calculée?",
          answer: "La date libre de dettes est calculée en divisant votre dette totale par le montant de votre paiement mensuel. Cela nous donne le nombre de mois nécessaires pour rembourser la dette, que nous ajoutons ensuite à la date d'aujourd'hui pour déterminer votre date exacte libre de dettes."
        },
        {
          question: "Que signifient les indicateurs de statut?",
          answer: "SUR LA BONNE VOIE signifie que votre dette sera remboursée dans les 36 mois avec des paiements durables.\nEN DIFFICULTÉ indique que votre calendrier de remboursement s'étend au-delà de 36 mois mais reste réalisable.\nNON DURABLE signifie que votre paiement mensuel est trop faible par rapport au montant de votre dette, ce qui entraîne un calendrier de remboursement irréaliste."
        },
        {
          question: "Dois-je inclure tous les types de dettes?",
          answer: "Oui, incluez toutes les dettes de consommation telles que les cartes de crédit, les prêts personnels, les prêts automobiles et les prêts étudiants. Cependant, la dette hypothécaire est généralement calculée séparément en raison de sa nature à long terme et de ses différentes structures d'intérêt."
        },
        {
          question: "Comment puis-je améliorer mon calendrier de remboursement de dettes?",
          answer: "Vous pouvez accélérer la liberté de dette en augmentant le montant de votre paiement mensuel, en consolidant les dettes à taux d'intérêt élevé, en réduisant les dépenses inutiles pour libérer plus d'argent pour les paiements de dettes, ou en utilisant des gains inattendus comme des remboursements d'impôts ou des primes pour des paiements supplémentaires."
        },
        {
          question: "Ce calculateur tient-il compte des intérêts?",
          answer: "Ce calculateur fournit un calendrier simplifié basé sur le montant de votre paiement actuel. Pour des calculs précis incluant les taux d'intérêt et les effets d'intérêt composé, consultez un conseiller financier ou utilisez des outils spécialisés de remboursement de dettes."
        }
      ]
    },
    de: {
      title: "Häufig Gestellte Fragen",
      faqs: [
        {
          question: "Wie wird das schuldenfreie Datum berechnet?",
          answer: "Das schuldenfreie Datum wird berechnet, indem Ihre Gesamtschuld durch den Betrag Ihrer monatlichen Zahlung geteilt wird. Dies gibt uns die Anzahl der Monate, die zum Abbezahlen der Schulden benötigt werden, die wir dann zum heutigen Datum hinzufügen, um Ihr genaues schuldenfreies Datum zu bestimmen."
        },
        {
          question: "Was bedeuten die Statusindikatoren?",
          answer: "AUF KURS bedeutet, dass Ihre Schulden innerhalb von 36 Monaten mit nachhaltigen Zahlungen abbezahlt werden.\nKÄMPFEND zeigt an, dass Ihr Rückzahlungszeitplan über 36 Monate hinausgeht, aber erreichbar bleibt.\nNICHT NACHHALTIG bedeutet, dass Ihre monatliche Zahlung im Verhältnis zu Ihrem Schuldenbetrag zu niedrig ist, was zu einem unrealistischen Rückzahlungszeitplan führt."
        },
        {
          question: "Sollte ich alle Arten von Schulden einbeziehen?",
          answer: "Ja, schließen Sie alle Verbraucherschulden wie Kreditkarten, Privatkredite, Autokredite und Studienkredite ein. Hypothekenschulden werden jedoch in der Regel separat berechnet, da sie langfristiger Natur sind und unterschiedliche Zinsstrukturen aufweisen."
        },
        {
          question: "Wie kann ich meinen Schuldenrückzahlungszeitplan verbessern?",
          answer: "Sie können die Schuldenfreiheit beschleunigen, indem Sie den Betrag Ihrer monatlichen Zahlung erhöhen, hochverzinsliche Schulden konsolidieren, unnötige Ausgaben reduzieren, um mehr Geld für Schuldenzahlungen freizusetzen, oder unerwartete Gewinne wie Steuerrückerstattungen oder Boni für zusätzliche Zahlungen verwenden."
        },
        {
          question: "Berücksichtigt dieser Rechner Zinsen?",
          answer: "Dieser Rechner bietet einen vereinfachten Zeitplan basierend auf Ihrem aktuellen Zahlungsbetrag. Für genaue Berechnungen einschließlich Zinssätzen und Zinseszinseffekten konsultieren Sie einen Finanzberater oder verwenden Sie spezialisierte Schuldenrückzahlungstools."
        }
      ]
    }
  };

  const t = translations?.[currentLanguage] || translations?.en;

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-6 md:mb-8 text-center">
        {t?.title}
      </h2>
      <div className="space-y-4">
        {t?.faqs?.map((faq, index) => (
          <div
            key={index}
            className="bg-card rounded-lg shadow-elevation-1 overflow-hidden"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full flex items-center justify-between p-4 md:p-6 text-left transition-smooth hover:bg-muted/50"
            >
              <span className="text-base md:text-lg font-medium text-foreground pr-4">
                {faq?.question}
              </span>
              <Icon
                name={openIndex === index ? "ChevronUp" : "ChevronDown"}
                size={20}
                color="var(--color-foreground)"
                className="flex-shrink-0"
              />
            </button>

            {openIndex === index && (
              <div className="px-4 md:px-6 pb-4 md:pb-6">
                <p className="text-sm md:text-base text-muted-foreground whitespace-pre-line">
                  {faq?.answer}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQSection;